/**
 * MiniProjectBudgetTab — Task #232.
 *
 * Body for the Budget sub-tab inside MiniProjectKpiNavigation.
 * Relocation of BudgetModal's body into the inline sub-tab pattern
 * established by Schedule (#229) and Mission (#230), wired to the
 * Lead-editable BomSection for granular work-item editing.
 *
 * Composition (top → bottom), following the locked sub-tab pattern of
 * "macro signal → granular work items":
 *
 *   1. Spend vs Budget progress bar (migration 190) — when planned
 *      budget is set, shows spend / planned %, color-coded by the same
 *      thresholds the StatusWriter auto-suggest uses.
 *   2. Stats card — counts row (Needed / Obtained / optionally N/A /
 *      Total).
 *   3. Estimated-cost rollup card — per-currency table (obtained vs
 *      still-needed vs total). Only rendered when at least one item
 *      has cost data; most BOMs start without it.
 *   4. Recorded-spend card (migration 189) — per-currency actuals
 *      sourced from BOM item actualTotal. Only rendered when at
 *      least one item has actuals recorded.
 *   5. Resources section card — section header + Lead-only "+" button
 *      + BomSection. Section label is "Resources" per
 *      POLICY_RESOURCE_VOCABULARY.md §4 — the Lead edits Resources,
 *      which roll up to Budget.
 *
 * The earlier grey-note disclaimer about the KPI dot being hardcoded
 * grey was removed 2026-05-28 — migration 190 wired the dot to the
 * Lead's qualitative budget_assessment on the latest status, so the
 * "grey by design" caveat no longer applies.
 *
 * Actuals (Migration 189, Skip 2026-05-28):
 *   The Lead can now record actual_cost / actual_quantity / purchase_date
 *   on each BOM item via the edit tray. When any item carries an actual,
 *   the tab grows a "Recorded spend" card showing per-currency totals of
 *   the server-computed `actualTotal` field. The tab does NOT show
 *   placeholder columns when no actuals exist yet — same honest pattern
 *   that drove the original "no fake columns" decision; the card only
 *   appears once there is real data to put in it.
 *
 *   ActualTotal is computed server-side:
 *     ActualTotal = ActualCost × (ActualQuantity ?? Quantity ?? 1)
 *   The fallback to planned Quantity means a Lead who got exactly what
 *   they ordered doesn't need to re-enter quantity to see a rollup.
 *
 *   Budget KPI dot remains hardcoded grey by Skip's 2026-05-26 product
 *   call (see computeKpiStatus.ts:155). Threshold decision waits for
 *   pilot data — recording actuals here is what builds that pilot data.
 *
 * Strings reuse `miniProject.kpiModals.budget.*` keys — same
 * convention Mission and Schedule sub-tabs follow when relocating
 * modal content into a tab. Actual-spend strings use `defaultValue`
 * fallbacks so the tab ships without a separate add-i18n-key.cjs run;
 * canonical keys can be scaffolded later via the script.
 */

import { useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type {
  MiniProject,
  MiniProjectBomItem,
} from "../../api/miniProjects";
import BomSection from "../../Pages/MiniProjectDetail/sections/BomSection";
import styles from "./MiniProjectBudgetTab.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  miniProject: MiniProject;
  bomItems: MiniProjectBomItem[];
  isLead: boolean;
  /** Refetch trigger for BOM mutations — delegates to the parent's
   *  sub.refetch since BOM items live on the shared sub-entities hook. */
  onBomMutated: () => void;
  /** Add-tray open state for the Resources (BOM) section, lifted to
   *  the parent so the section header "+" can trigger it without
   *  BomSection owning the toggle (same pattern Schedule uses for
   *  TasksSection and Mission uses for GoalsSection). */
  addingBomItem: boolean;
  onOpenAddBomItem: () => void;
  onCloseAddBomItem: () => void;
}

/* ── Helpers ───────────────────────────────────────────────────── */

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toFixed(0)} ${currency}`;
  }
}

/* ── Component ─────────────────────────────────────────────────── */

export default function MiniProjectBudgetTab({
  miniProject,
  bomItems,
  isLead,
  onBomMutated,
  addingBomItem,
  onOpenAddBomItem,
  onCloseAddBomItem,
}: Props) {
  const { t } = useAppTranslation("pages");

  const counts = useMemo(() => {
    let needed = 0, obtained = 0, na = 0;
    for (const i of bomItems) {
      if (i.procurementState === "obtained") obtained++;
      else if (i.procurementState === "needed") needed++;
      else na++;
    }
    return { needed, obtained, na, total: bomItems.length };
  }, [bomItems]);

  // Per-currency totals. Only sums items where cost is non-null —
  // otherwise we'd be inventing data. Items with cost but no quantity
  // default quantity to 1, matching BudgetModal's behavior.
  const costByCurrency = useMemo(() => {
    const m = new Map<string, { obtained: number; needed: number }>();
    for (const i of bomItems) {
      if (i.estimatedCost == null) continue;
      const qty = i.quantity ?? 1;
      const subtotal = i.estimatedCost * qty;
      const ccy = (i.currencyCode || "USD").toUpperCase();
      const slot = m.get(ccy) || { obtained: 0, needed: 0 };
      if (i.procurementState === "obtained") slot.obtained += subtotal;
      else if (i.procurementState === "needed") slot.needed += subtotal;
      m.set(ccy, slot);
    }
    return m;
  }, [bomItems]);

  const hasAnyCost = costByCurrency.size > 0;

  // Migration 189: per-currency actual spend rollup. Reads the
  // server-computed `actualTotal` directly so the client doesn't
  // have to re-derive the cost × quantity math. Items without an
  // actualTotal are skipped silently. Empty Map → don't render the
  // "Recorded spend" card.
  const actualByCurrency = useMemo(() => {
    const m = new Map<string, number>();
    let itemsWithActuals = 0;
    for (const i of bomItems) {
      if (i.actualTotal == null) continue;
      itemsWithActuals++;
      const ccy = (i.currencyCode || "USD").toUpperCase();
      m.set(ccy, (m.get(ccy) ?? 0) + i.actualTotal);
    }
    return { totals: m, itemCount: itemsWithActuals };
  }, [bomItems]);

  const hasAnyActual = actualByCurrency.totals.size > 0;

  // Migration 190 — top progress bar. Compares recorded spend against
  // the planned budget set in General settings. Currency-aware: only
  // sums BOM items whose currency_code matches plannedBudgetCurrency,
  // so items in other currencies don't pollute the rollup. They still
  // appear in the per-currency Recorded-spend card below.
  const budgetProgress = useMemo(() => {
    const planned = miniProject.plannedBudgetAmount;
    const plannedCcy = (miniProject.plannedBudgetCurrency || "USD").toUpperCase();
    if (planned == null || planned <= 0) {
      return { hasPlan: false, planned: 0, plannedCcy, spent: 0, pct: 0, color: "default" as const };
    }
    let spent = 0;
    for (const i of bomItems) {
      if (i.actualTotal == null) continue;
      const itemCcy = (i.currencyCode || "USD").toUpperCase();
      if (itemCcy !== plannedCcy) continue;
      spent += i.actualTotal;
    }
    const pct = (spent / planned) * 100;
    // Same thresholds as the StatusWriter auto-suggest.
    const color: "success" | "warning" | "error" =
      pct < 100 ? "success" : pct <= 110 ? "warning" : "error";
    return { hasPlan: true, planned, plannedCcy, spent, pct, color };
  }, [bomItems, miniProject.plannedBudgetAmount, miniProject.plannedBudgetCurrency]);

  return (
    <div className={styles.root}>
      {/* ── Top progress bar (Migration 190) ──────────────── */}
      <div className={styles.progressCard}>
        <div className={styles.eyebrow}>
          {t("miniProject.kpiModals.budget.spendProgress", {
            defaultValue: "Spend vs Budget",
          })}
        </div>
        {budgetProgress.hasPlan ? (
          <>
            <div className={styles.progressBarTrack}>
              <div
                className={`${styles.progressBarFill} ${styles[`progressFill_${budgetProgress.color}`]}`}
                style={{ inlineSize: `${Math.min(100, budgetProgress.pct)}%` }}
              />
              {budgetProgress.pct > 100 && (
                <div
                  className={`${styles.progressBarOverflow} ${styles[`progressFill_${budgetProgress.color}`]}`}
                  // Pin the overflow indicator at the right edge so the
                  // Lead sees a visual signal that we've blown past 100%
                  // even when the fill width is clipped to 100%.
                  aria-hidden="true"
                />
              )}
            </div>
            <div className={styles.progressLegend}>
              <span>
                <strong className={styles[`progressText_${budgetProgress.color}`]}>
                  {budgetProgress.pct.toFixed(0)}%
                </strong>{" "}
                <span style={{ color: "var(--text-muted)" }}>
                  {t("miniProject.kpiModals.budget.spendProgressMeta", {
                    spent: formatMoney(budgetProgress.spent, budgetProgress.plannedCcy),
                    planned: formatMoney(budgetProgress.planned, budgetProgress.plannedCcy),
                    defaultValue: `${formatMoney(budgetProgress.spent, budgetProgress.plannedCcy)} of ${formatMoney(budgetProgress.planned, budgetProgress.plannedCcy)} spent`,
                  })}
                </span>
              </span>
            </div>
          </>
        ) : (
          <div className={styles.progressEmpty}>
            {t("miniProject.kpiModals.budget.noPlanHint", {
              defaultValue:
                "Set a planned budget in General settings to see spend progress.",
            })}
          </div>
        )}
      </div>

      {/* ── Stats card ─────────────────────────────────────── */}
      <div className={styles.statsCard}>
        <div className={styles.eyebrow}>
          {t("miniProject.kpiModals.budget.title")}
        </div>
        {bomItems.length === 0 ? (
          <div className={styles.emptyInline}>
            <div className={styles.emptyTitle}>
              {t("miniProject.kpiModals.budget.empty.title")}
            </div>
            <div className={styles.emptyHint}>
              {isLead
                ? t("miniProject.kpiModals.budget.empty.leadHint")
                : t("miniProject.kpiModals.budget.empty.publicHint")}
            </div>
          </div>
        ) : (
          <div className={styles.statRow}>
            <Stat
              label={t("miniProject.kpiModals.budget.stat.needed")}
              value={counts.needed}
              variant="warning"
            />
            <Stat
              label={t("miniProject.kpiModals.budget.stat.obtained")}
              value={counts.obtained}
              variant="success"
            />
            {counts.na > 0 && (
              <Stat
                label={t("miniProject.kpiModals.budget.stat.notApplicable")}
                value={counts.na}
                variant="muted"
              />
            )}
            <Stat
              label={t("miniProject.kpiModals.budget.stat.totalItems")}
              value={counts.total}
              variant="default"
            />
          </div>
        )}
      </div>

      {/* ── Cost rollup card (only if any item has cost) ──── */}
      {hasAnyCost && (
        <div className={styles.costCard}>
          <div className={styles.eyebrow}>
            {t("miniProject.kpiModals.budget.cost.title")}
          </div>
          <table className={styles.costTable}>
            <thead>
              <tr>
                <th>{t("miniProject.kpiModals.budget.cost.currency")}</th>
                <th className={styles.numCell}>
                  {t("miniProject.kpiModals.budget.cost.obtained")}
                </th>
                <th className={styles.numCell}>
                  {t("miniProject.kpiModals.budget.cost.stillNeeded")}
                </th>
                <th className={styles.numCell}>
                  {t("miniProject.kpiModals.budget.cost.total")}
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.from(costByCurrency.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([ccy, v]) => (
                  <tr key={ccy}>
                    <td>{ccy}</td>
                    <td className={styles.numCell}>
                      {formatMoney(v.obtained, ccy)}
                    </td>
                    <td className={styles.numCell}>
                      {formatMoney(v.needed, ccy)}
                    </td>
                    <td className={styles.numCell}>
                      {formatMoney(v.obtained + v.needed, ccy)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Recorded-spend card (Migration 189) ─────────────────
          Only rendered when at least one BOM item carries an actual.
          Same chrome family as the planned cost card above so the eye
          reads them as related. Per-currency sum of the server-computed
          actualTotal field — no client-side math. */}
      {hasAnyActual && (
        <div className={styles.costCard}>
          <div className={styles.eyebrow}>
            {t("miniProject.kpiModals.budget.actual.title", {
              defaultValue: "Recorded spend",
            })}
          </div>
          <table className={styles.costTable}>
            <thead>
              <tr>
                <th>{t("miniProject.kpiModals.budget.cost.currency")}</th>
                <th className={styles.numCell}>
                  {t("miniProject.kpiModals.budget.actual.spent", {
                    defaultValue: "Spent",
                  })}
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.from(actualByCurrency.totals.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([ccy, total]) => (
                  <tr key={ccy}>
                    <td>{ccy}</td>
                    <td className={styles.numCell}>
                      {formatMoney(total, ccy)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className={styles.actualFootnote}>
            {t("miniProject.kpiModals.budget.actual.itemCount", {
              count: actualByCurrency.itemCount,
              total: bomItems.length,
              defaultValue: `${actualByCurrency.itemCount} of ${bomItems.length} items have actuals recorded`,
            })}
          </div>
        </div>
      )}

      {/* ── Resources section card ─────────────────────────── */}
      <div className={styles.resourcesCard}>
        <div className={styles.resourcesHeader}>
          <div className={styles.eyebrow}>
            {t("miniProject.progress.section.resources")}
          </div>
          {isLead && (
            <button
              type="button"
              onClick={onOpenAddBomItem}
              aria-label={t("miniProject.progress.section.addResource")}
              className={styles.addBtn}
            >
              +
            </button>
          )}
        </div>
        <BomSection
          miniProjectGuid={miniProject.miniProjectGuid}
          bomItems={bomItems}
          isLead={isLead}
          onMutated={onBomMutated}
          addOpen={addingBomItem}
          onCloseAdd={onCloseAddBomItem}
        />
      </div>

    </div>
  );
}

/* ── Subcomponents ─────────────────────────────────────────────── */

function Stat({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant: "warning" | "success" | "muted" | "default";
}) {
  return (
    <div className={`${styles.stat} ${styles[`stat_${variant}`]}`}>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}
